import classNames from "classnames";
import { styled } from "@linaria/react";
import React from "react";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";
import { color } from "..";
import TooltipV2 from "../TooltipV2";
import OverlayWrapper from "../TooltipV2/OverlayWrapper";
import { fonts } from "../Theme";
import { css } from "@linaria/core";

const DISABLED_CN = "disabled";
const WITH_CHILDREN_CN = "with-children";
const PARTIAL_CN = "partial";
const READONLY_CN = "readonly";

const Wrapper = styled.label`
  display: inline-flex;
  align-items: center;
  overflow: hidden; /* allow ellipsis for label content */
  flex: none;
  width: 100%;
  color: ${color.carbon800};
  font: ${fonts.body1};

  :not(.${DISABLED_CN}) {
    cursor: pointer;
  }
  &.${DISABLED_CN} {
    cursor: not-allowed;
    opacity: 0.5;
  }
  &.${READONLY_CN} {
    pointer-events: none;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const checked = css`
  input:checked ~ .indicatorWrapper .indicator {
    border-color: ${color.lightNitrogen500_Main};
    background-color: ${color.lightNitrogen500_Main};
  }

  input:checked ~ .indicatorWrapper .indicator::after {
    content: "";
    display: block;
    position: relative;
    left: 50%;
    top: 50%;
    width: 6px;
    height: 14px;
    border: 1px solid;
    border-color: ${color.hydrogen};
    border-width: 0 2px 2px 0;
    transform: translate(-50%, -8px) rotate(45deg);
    clip-path: inset(0);
    transition: 200ms ease clip-path;
  }

  input:checked ~ .indicatorWrapper .indicator.smallIndicator::after {
    width: 5px;
    height: 11px;
    transform: translate(-50%, -7px) rotate(45deg);
  }
`;

export const Indicator = styled.div`
  flex: none;
  width: 20px;
  height: 20px;
  vertical-align: middle;
  border: 1px solid;
  border-color: ${color.titanium400};
  border-radius: 4px;

  input ~ & {
    transition: 100ms ease background-color;
  }

  .${PARTIAL_CN} & {
    background-color: ${color.titanium400};
    border: 1px solid;
    border-color: ${color.titanium400};
    border-radius: 4px;
    ::after {
      content: "";
      display: block;
      position: relative;
      top: 8px;
      margin: auto;
      height: 2px;
      width: 10px;
      background-color: ${color.hydrogen};
    }
  }

  .${WITH_CHILDREN_CN} & {
    margin-right: 8px;
  }
`;

const SmallIndicator = css`
  width: 16px;
  height: 16px;

  .${PARTIAL_CN} & {
    ::after {
      width: 8px;
      top: 6px;
    }
  }
`;

const indicatorWrapper = css`
  display: flex;
  align-items: center;
`;

const Checkbox = (
  {
    children,
    className,
    disabled,
    tabIndex,
    readOnly,
    partial,
    overlay,
    indicatorClass,
    smallIndicator,
    ...props
  }: React.InputHTMLAttributes<HTMLInputElement> & { partial?: boolean } & { overlay?: string } & {
    indicatorClass?: string;
  } & { smallIndicator?: boolean },
  forwardedInputRef: React.Ref<HTMLInputElement>,
) => {
  const inputRef = useEnsuredForwardedRef(forwardedInputRef);
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (readOnly) return;
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        // replicate default HTMLSelect submit form behavior
        const submitButton = inputRef.current
          ?.closest("form")
          ?.querySelector("button[type=submit]") as HTMLButtonElement | null;
        submitButton?.click();
      }

      if (e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        inputRef.current?.click();
      }
    },
    [inputRef, readOnly],
  );

  const handleLabelClick = React.useCallback((e: React.MouseEvent<HTMLLabelElement>) => {
    // if we don't, 2 events propagate: one from the label, and a second one from the input himself (implicit input click from UA).
    // it's a nightmare to filter if checkbox is included in a larger clickable area.
    // Since we never use the propagation, better have none than 2.
    // is it a bug? IDK. Not even sure it happens reliably cross-browser.
    e.stopPropagation();
  }, []);

  return (
    <Wrapper
      className={classNames(className, checked, {
        [READONLY_CN]: readOnly,
        [DISABLED_CN]: disabled,
        [WITH_CHILDREN_CN]: Boolean(children),
        [PARTIAL_CN]: partial,
      })}
      tabIndex={tabIndex === undefined ? (disabled ? -1 : 0) : tabIndex}
      onKeyDown={handleKeyDown}
      onClick={handleLabelClick}
    >
      <HiddenInput
        tabIndex={-1}
        ref={inputRef}
        type="checkbox"
        {...props}
        disabled={disabled}
        readOnly={readOnly}
      />
      <TooltipV2
        style={{ visibility: overlay === undefined ? "hidden" : "visible" }}
        overlay={<OverlayWrapper text={overlay} />}
      >
        <div className={classNames(indicatorWrapper, "indicatorWrapper")}>
          <Indicator
            className={classNames(indicatorClass, "indicator", {
              [SmallIndicator]: smallIndicator,
              smallIndicator,
            })}
            data-cy="auto-diag-checkbox"
          />

          <span>{children}</span>
        </div>
      </TooltipV2>
    </Wrapper>
  );
};

export default React.memo(React.forwardRef(Checkbox));
